import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { PublicShell } from './PublicShell';

/** The pricing page, fetched rather than mirrored.
 *
 *  It used to be a hand-maintained copy of the figures in app/core/plans.py,
 *  with a comment admitting it and asking whoever changed a price to remember to
 *  change this file too. Nothing checked that they had. Now it reads
 *  GET /v1/pricing, which resolves the same catalogue the platform bills and
 *  enforces against, so a price changed in the admin console is a price shown
 *  here.
 *
 *  What stays local is the *copy* — the one-line pitch, the "most popular" tag,
 *  and the feature bullets that are not numbers. Those are editorial and belong
 *  in the marketing site, not in a billing table.
 */

type PricedPlan = {
  key: string;
  label: string;
  price_cents: number | null;
  included_volume: number;
  overage_per_thousand_cents: number | null;
  custom_domains: number | null;
  webhooks: number | null;
  team_seats: number | null;
  dedicated_ip: string;
  hard_volume_cap: boolean;
  self_serve: boolean;
};

/** Editorial copy per plan. Keyed by plan key, with a fallback: a plan created
 *  in the admin console gets a generic pitch rather than a blank card. */
const COPY: Record<
  string,
  { desc: string; extras: string[]; tag?: string; featured?: boolean }
> = {
  free: {
    desc: 'For trying things out.',
    extras: ['REST API & SMTP'],
  },
  starter: {
    desc: 'For small, growing products.',
    extras: ['REST API & SMTP', 'Webhooks & analytics'],
  },
  pro: {
    desc: 'For teams sending real volume.',
    tag: 'Most popular',
    featured: true,
    extras: ['Audiences & broadcasts', 'Priority support'],
  },
  enterprise: {
    desc: 'For high-volume senders.',
    extras: ['SSO', 'Audit log', 'Custom contract'],
  },
};

const FALLBACK_COPY = { desc: 'A plan built for your volume.', extras: ['REST API & SMTP'] };

function priceOf(plan: PricedPlan): { price: string; per: string } {
  if (plan.price_cents === null) return { price: 'Custom', per: '' };
  const dollars = plan.price_cents / 100;
  // Whole dollars where the price is whole, cents where it is not: $19, and $19.50
  // rather than $19.5 or $19.00.
  const shown = Number.isInteger(dollars) ? dollars.toLocaleString() : dollars.toFixed(2);
  return { price: `$${shown}`, per: '/month' };
}

/** The bullets that come from the numbers, in the order they matter. */
function derivedFeatures(plan: PricedPlan): string[] {
  const out = [`${plan.included_volume.toLocaleString()} emails/month`];

  if (plan.custom_domains === null) out.push('Unlimited custom domains');
  else out.push(`${plan.custom_domains} custom domain${plan.custom_domains === 1 ? '' : 's'}`);

  if (plan.team_seats === null) out.push('Unlimited team seats');
  else out.push(`${plan.team_seats} team seat${plan.team_seats === 1 ? '' : 's'}`);

  if (plan.dedicated_ip === 'included') out.push('Dedicated IP included');
  else if (plan.dedicated_ip === 'add_on') out.push('Dedicated IP (add-on)');

  // Said plainly, because it is the difference between a billing event and an
  // outage: on a capped plan, running out stops the mail.
  if (!plan.hard_volume_cap && plan.overage_per_thousand_cents !== null) {
    const rate = plan.overage_per_thousand_cents / 100;
    out.push(
      `Then $${Number.isInteger(rate) ? rate : rate.toFixed(2)} per 1,000 — sending never stops`,
    );
  }

  return out;
}

export function Pricing() {
  const [plans, setPlans] = useState<PricedPlan[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const response = await fetch('/v1/pricing');
        if (!response.ok) throw new Error(String(response.status));
        const body = (await response.json()) as { plans: PricedPlan[] };
        if (live) setPlans(body.plans);
      } catch {
        // No fallback figures. A stale price on a pricing page is an offer we
        // might not honour, which is worse than admitting we cannot show one.
        if (live) setFailed(true);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  return (
    <PublicShell>
      <div className="page-head">
        <span className="eyebrow">Pricing</span>
        <h1>Simple pricing that scales with you</h1>
        <p>
          Start free. Pay for volume as you grow — no surprise fees, no long-term contracts.
        </p>
      </div>

      {failed && (
        <p className="faq-note">
          Our prices are not loading right now. Email{' '}
          <a href="mailto:sales@mailgiri.com">sales@mailgiri.com</a> and we will send
          them over.
        </p>
      )}

      {plans === null && !failed && <p className="faq-note">Loading prices…</p>}

      {plans !== null && (
        <div className="plans">
          {plans.map((plan) => {
            const copy = COPY[plan.key] ?? FALLBACK_COPY;
            const { price, per } = priceOf(plan);
            const features = [...derivedFeatures(plan), ...copy.extras];
            const tag = 'tag' in copy ? copy.tag : undefined;
            const featured = 'featured' in copy ? copy.featured : false;

            return (
              <div className={featured ? 'plan featured' : 'plan'} key={plan.key}>
                {tag && <span className="featured-tag">{tag}</span>}
                <div className="plan-name">{plan.label}</div>
                <div className="plan-price">
                  {price}
                  {per && <span>{per}</span>}
                </div>
                <div className="plan-desc">{copy.desc}</div>

                {plan.self_serve || plan.price_cents === 0 ? (
                  <Link className="plan-cta secondary" to="/signup">
                    Get started
                    <span className="sr-only"> on the {plan.label} plan</span>
                  </Link>
                ) : (
                  // Not a signup link. A plan the checkout cannot sell would drop
                  // someone into a flow that ends nowhere, so it goes to a person.
                  <a className="plan-cta secondary" href="mailto:sales@mailgiri.com">
                    Contact sales
                    {/* The visible label alone reads as "Contact sales" out of
                        context; this says which plan it belongs to. */}
                    <span className="sr-only"> about the {plan.label} plan</span>
                  </a>
                )}

                <ul className="plan-features">
                  {features.map((feature) => (
                    <li key={feature}>
                      {/* Decorative: every row in this list is included, so a tick
                          read before each line adds nothing but noise. */}
                      <span className="tick" aria-hidden="true">
                        ✓
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <p className="faq-note">
        All plans include suppression handling, bounce/complaint tracking, and full API access.
      </p>
    </PublicShell>
  );
}
